import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, debounceTime, filter, map, Observable, Subject, switchMap, take, takeUntil} from 'rxjs';
import {dispatchDeletedItemEvent, dispatchSavedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
    AangifteBerekening,
    AangifteRapport,
    BokkzAlgDataService,
    BokkzClient,
    BokkzIbDataService,
    FeatureFlagService,
    FeatureServiceComponent,
    IncomeTaxEntity,
    MenuInterface,
    SubOnderdeel,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute, Router} from '@angular/router';
import {ContextMenuConfiguration} from '@snelstart/ui-context-menu';
import {FormBuilder} from '@angular/forms';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule, NgStyle} from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-tax-income-list',
    templateUrl: './feature-tax-income-list.component.html',
    imports: [SharedUiTableModule, SharedUiButtonModule, NgStyle, CommonModule, FeatureServiceComponent],
    styleUrls: ['./feature-tax-income-list.component.scss'],
})
export class FeatureTaxIncomeListComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    @Output() public details = new EventEmitter<string>();
    @Output() public hasData = new EventEmitter<boolean>();
    private destroy$ = new Subject<void>();
    protected tableConfig!: TableConfiguration<any, any>;
    public dialogVisible = false;
    public showTable = false;
    public year = {} as number;
    public actualYear = new Date().getFullYear();
    public options$ = new BehaviorSubject<MenuInterface[]>([]);
    public options = [] as MenuInterface[];
    public incomeTaxEntity = {} as IncomeTaxEntity;
    public featureFlagToegang = false;
    public clientId: any;
    public berekening = {} as AangifteBerekening;
    public rapport = {} as AangifteRapport;
    public rapportPartner = {} as AangifteRapport;
    public hasFiscalPartner = false;
    public sumOverigInkomen = 0;
    public sumOverigInkomenPartner = 0;
    public sumEigenWoningAangever = 0;
    public sumEigenWoningPartner = 0;
    public sumPremiesInkomensvoorzieningen = 0;
    public sumPremiesInkomensvoorzieningenPartner = 0;
    public sumBox2Aangever = 0;
    public sumBox2Partner = 0;
    public sumPGAAangever = 0;
    public sumPGAPartner = 0;
    public sumVAIB: number | undefined = 0;
    public sumVAZVW: number | undefined = 0;
    public sumDivBel: number | undefined = 0;
    public sumVAIBPartner: number | undefined = 0;
    public sumVAZVWPartner: number | undefined = 0;
    public sumDivBelPartner: number | undefined = 0;
    public sumVoorheffingen = 0;
    public sumVoorheffingenPartner = 0;
    public sumAanslagenIbenZvw = 0;
    public sumIB: number | undefined = 0;
    public sumZVW: number | undefined = 0;
    public sumAanslagenIbenZvwPartner = 0;
    public sumIBPartner: number | undefined = 0;
    public sumZVWPartner: number | undefined = 0;
    public bedragRevisierente: number | undefined = 0
    public bedragRevisierentePartner: number | undefined = 0
    public labelAangever = 'Aangever & Partner';
    public isDefAangifte: any;
    public gezamenlijk = false;
    public naamAangever = '';
    public naamPartner = '';
    public activePage: null | string = '';

    constructor(
        public dataService: BokkzIbDataService,
        public dataAlgService: BokkzAlgDataService,
        private router: Router,
        public fb: FormBuilder,
        private bokkzClient: BokkzClient,
        private algDataService: BokkzAlgDataService,
        private activatedRoute: ActivatedRoute,
        private featureFlagService: FeatureFlagService,
        private changeDetector: ChangeDetectorRef,
    ) {
    }

    ngOnInit(): void {
        this.featureFlagService
            .hasFeatureFlag('Venture_Fiscaal_Inkomstenbelasting')
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                this.featureFlagToegang = response;
            });
        this.algDataService.selectedYear.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.year = value;
            this.showTable = false;
            this.hasData.emit(false);
            this.dataService.hasVerdeling = false;
            this.dataService.updateAangifteBerekening({})
            this.dataService.updateAangifteRapport({});
            setTimeout(() => {
                this.createTableConfig();
                this.showTable = true;
            }, 0);
        });
        this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            this.id = params['incometaxReturnId'];
            this.clientId = params['clientId'];
        });
        this.dataService.incomeTaxEntity
            .pipe(takeUntil(this.destroy$))
            .pipe(debounceTime(500))
            .subscribe((incomeTaxEntity) => {
                this.incomeTaxEntity = incomeTaxEntity;
                this.hasData.emit(!!incomeTaxEntity.aangifte);
                if (incomeTaxEntity.aangifte) {
                    const value = incomeTaxEntity.aangifte;
                    if (value.aangever.geboortedatum) {
                        this.dataService.getBerekening(value);
                    }
                    if (value.partnerAangifte) {
                        this.rapportPartner = value.partnerAangifte;
                        this.getNaamPartner();
                        this.getSumOverigeInkomstenPartner();
                        this.getEigenWoningPartner();
                        this.getBox2Partner();
                        this.getSumPGAPartner();
                        this.getSumVoorheffingenPartner();
                        this.getSumPremiesInkomensvoorzieningenPartner();
                        this.getRevisieRentePartner();
                        this.getSumAanslagenIbenZvwPartner;
                    }
                    this.getNaamAangever(incomeTaxEntity);
                    this.getSumOverigeInkomsten();
                    this.getEigenWoningAangever();
                    this.getBox2Aangever();
                    this.getSumPGAAangever();
                    this.getSumPremiesInkomensvoorzieningen();
                    this.getSumVoorheffingen();
                    this.getRevisieRenteAangever();
                    this.getSumAanslagenIbenZvw();

                    if (
                        incomeTaxEntity.aangifte?.gezamenlijkeAangifte !== undefined &&
                        incomeTaxEntity.aangifte?.gezamenlijkeAangifte
                    ) {
                        this.gezamenlijk = true;
                    }
                }
                this.fetchData();
            });

        this.dataService.aangifteBerekening.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.berekening = value;
            this.getSumOverigeInkomsten();
            this.getEigenWoningAangever();
            this.getBox2Aangever();
            this.getSumPGAAangever();
            this.getSumVoorheffingen();
            this.getSumPremiesInkomensvoorzieningen();
            this.getSumAanslagenIbenZvw();
        });
        this.dataService.aangifteRapport.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.rapport = value;
            this.getSumOverigeInkomsten();
            this.getEigenWoningPartner();
            this.getBox2Partner();
            this.getSumPGAAangever();
            this.getSumVoorheffingen();
            this.getSumPremiesInkomensvoorzieningen();
            this.getSumAanslagenIbenZvwPartner();
        });
        this.dataService.isDefAangifte.pipe(takeUntil(this.destroy$)).subscribe((isDefAangifte) => {
            this.isDefAangifte = isDefAangifte;
            setTimeout(() => {
                this.createTableConfig();
                this.showTable = true;
            }, 0);
        });
        this.dataService.menuInterface.pipe(takeUntil(this.destroy$)).subscribe((menuInterface) => {
            const old = this.options$.value;
            if (JSON.stringify(this.options$.value) != JSON.stringify(menuInterface)) {
                this.options$.next([...menuInterface.reverse()]);
                this.changeDetector.detectChanges();
                for (let i = 0; i < menuInterface.length; i++) {
                    if (JSON.stringify(old[i]) != JSON.stringify(menuInterface[i])) {
                        dispatchSavedItemEvent({
                            identifier: menuInterface[i].value,
                            result: menuInterface[i] as unknown as Record<number, MenuInterface>,
                        });
                        if (!menuInterface[i].visible) {
                            dispatchDeletedItemEvent({
                                identifier: menuInterface[i].value,
                            });
                        }
                    }
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public navigateToSubPage(subOnderdeel: SubOnderdeel): void {
        const page = subOnderdeel.pageMapping;
        const url = `/tax-income/modules/${this.clientId}/${page}`;
        this.activePage = page === 'Aangifte jaar' ? null : page;
        this.router.navigate([url]).then(() => console.log('complete'));
    }

    public createTableConfig(): void {
        const criteria = new SearchCriteria();

        this.tableConfig = TableConfiguration.create({
            identifier: 'value',
            options: {
                useSearch: false,
                useSort: true,
            },
            criteria: criteria,

            search: () => {
                return this.bokkzClient.getIncomeTax(this.clientId, String(this.year)).pipe(
                    switchMap((incomeTaxEntity) => {
                        if (incomeTaxEntity.aangifte) {
                            this.dataService.updateIncomeTaxEntity(incomeTaxEntity);
                            if (incomeTaxEntity.aangifte.aangever.geboortedatum) {
                                this.dataService.getBerekening(incomeTaxEntity.aangifte);
                            }
                        } else {
                            this.options$.next([]);
                            this.dataService.updateAantalValidaties(0);
                        }

                        return this.options$.pipe(
                            filter((options) => Array.isArray(options)),
                            map((options) => {
                                const sortedGroups = options
                                    .filter((item) => item.visible)
                                    .map((group) => ({
                                        ...group,
                                        subonderdelen: (group.subonderdelen || [])
                                            .filter((sub) => sub.visible)
                                            .sort((a, b) => a.value - b.value),
                                    }))
                                    .sort((a, b) => a.value - b.value);
                                return new SearchResult(sortedGroups, sortedGroups.length);
                            }),
                            take(1),
                        );
                    }),
                );
            },

            details: (entity) => {
                if (entity) {
                    this.details.next(entity.label);
                }
            },

            fromDetailsMap: (entity) => {
                return entity as unknown as Record<number, MenuInterface>;
            },

            zeroStateConfig: ZeroStateConfiguration.create({
                icon: 'book-open-cover',
                mainText: this.year < 2024
                    ? 'Module niet beschikbaar'
                    : (this.year !== this.actualYear
                        ? $localize`Geen aangifte gevonden voor ` + this.year
                        : $localize`Geen verzoek gevonden voor ` + this.year),
                subText:
                    this.year < 2024
                        ? 'De aangifte inkomstenbelasting wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
                        : 'Maak een nieuwe aan en start direct met invullen.',
                buttonText: this.year < 2024 ? undefined : (this.year !== this.actualYear ? `Maak nieuwe aangifte` : 'Maak nieuw verzoek'),
                buttonConfig:
                    this.year < 2024
                        ? undefined
                        : ButtonConfiguration.create({
                            onClick: () => {
                                return new Observable((subscriber) => {
                                    setTimeout(() => {
                                        subscriber.next((this.dialogVisible = true));
                                        subscriber.complete();
                                    }, 50);
                                });
                            },
                        }),
            }),
        });
    }

    public getSumOverigeInkomsten() {
        let sumRow = 0;
        let sumPu = 0;
        if (this.berekening.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden !== undefined) {
            sumRow = this.berekening.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden;
        }
        if (this.berekening.box1?.opbouwInkomen?.periodiekeUitkeringen !== undefined) {
            sumPu = this.berekening.box1?.opbouwInkomen?.periodiekeUitkeringen;
        }
        this.sumOverigInkomen = sumPu + sumRow;
    }

    getSumOverigeInkomstenPartner() {
        let sumRow = 0;
        let sumPu = 0;
        if (this.berekening.partnerBerekening?.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden !== undefined) {
            sumRow = this.berekening.partnerBerekening?.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden;
        }
        if (this.berekening.partnerBerekening?.box1?.opbouwInkomen?.periodiekeUitkeringen !== undefined) {
            sumPu = this.berekening.partnerBerekening?.box1?.opbouwInkomen?.periodiekeUitkeringen;
        }
        this.sumOverigInkomenPartner = sumPu + sumRow;
    }

    getEigenWoningAangever() {
        let saldoEigenWoning: number | undefined = 0;
        let aftrekGOGEW: number | undefined = 0;
        if (this.incomeTaxEntity.aangifte?.box1?.belastbareInkomstenUitEigenWoning != null) {
            saldoEigenWoning = this.berekening?.box1?.opbouwInkomen?.inkomstenUitEigenWoning;
        }
        if (this.incomeTaxEntity.aangifte?.box1?.belastbareInkomstenUitEigenWoning != null) {
            aftrekGOGEW = this.berekening?.box1?.opbouwInkomen?.aftrekWegensGeenOfGeringeEigenWoningSchuld;
        }
        this.sumEigenWoningAangever = (saldoEigenWoning != undefined ? saldoEigenWoning : 0) - (aftrekGOGEW != undefined ? aftrekGOGEW : 0);
    }

    getEigenWoningPartner() {
        let saldoEigenWoning: number | undefined = 0;
        let aftrekGOGEW: number | undefined = 0;
        if (this.incomeTaxEntity.aangifte?.box1?.belastbareInkomstenUitEigenWoning != null) {
            saldoEigenWoning = this.berekening?.partnerBerekening?.box1?.opbouwInkomen?.inkomstenUitEigenWoning;
        }
        if (this.incomeTaxEntity.aangifte?.box1?.belastbareInkomstenUitEigenWoning != null) {
            aftrekGOGEW = this.berekening?.partnerBerekening?.box1?.opbouwInkomen?.aftrekWegensGeenOfGeringeEigenWoningSchuld;
        }
        this.sumEigenWoningPartner = (saldoEigenWoning != undefined ? saldoEigenWoning : 0) - (aftrekGOGEW != undefined ? aftrekGOGEW : 0);
    }

    getSumPremiesInkomensvoorzieningen() {
        let sumUitgInkVz = 0;
        let sumNegInkVz = 0;
        if (this.berekening.box1?.opbouwInkomen?.uitgavenVoorInkomensvoorzieningen !== undefined) {
            sumUitgInkVz = this.berekening.box1?.opbouwInkomen?.uitgavenVoorInkomensvoorzieningen;
        }
        if (this.berekening.box1?.opbouwInkomen?.negatieveUitgavenVoorInkomensvoorzieningen !== undefined) {
            sumNegInkVz = this.berekening.box1?.opbouwInkomen?.negatieveUitgavenVoorInkomensvoorzieningen;
        }
        this.sumPremiesInkomensvoorzieningen = sumUitgInkVz - sumNegInkVz;
    }

    getSumPremiesInkomensvoorzieningenPartner() {
        let sumUitgInkVz = 0;
        let sumNegInkVz = 0;
        if (this.berekening.partnerBerekening?.box1?.opbouwInkomen?.uitgavenVoorInkomensvoorzieningen !== undefined) {
            sumUitgInkVz = this.berekening.partnerBerekening?.box1?.opbouwInkomen?.uitgavenVoorInkomensvoorzieningen;
        }
        if (this.berekening.partnerBerekening?.box1?.opbouwInkomen?.negatieveUitgavenVoorInkomensvoorzieningen !== undefined) {
            sumNegInkVz = this.berekening.partnerBerekening?.box1?.opbouwInkomen?.negatieveUitgavenVoorInkomensvoorzieningen;
        }
        this.sumPremiesInkomensvoorzieningenPartner = sumUitgInkVz - sumNegInkVz;
    }

    getBox2Aangever() {
        let saldobox2: number | undefined = 0;
        if (this.incomeTaxEntity.aangifte?.box2?.aanmerkelijkBelang != undefined) {
            saldobox2 = this.berekening?.box2?.opbouwInkomen?.inkomenUitAanmerkelijkBelang;
        }
        this.sumBox2Aangever = saldobox2 != undefined ? saldobox2 : 0;
    }

    getBox2Partner() {
        let saldobox2: number | undefined = 0;
        if (this.incomeTaxEntity.aangifte?.box2?.aanmerkelijkBelang != null) {
            saldobox2 = this.berekening?.partnerBerekening?.box2?.opbouwInkomen?.inkomenUitAanmerkelijkBelang;
        }
        this.sumBox2Partner = saldobox2 != undefined ? saldobox2 : 0;
    }

    getSumPGAAangever() {
        if (this.incomeTaxEntity.aangifte?.verdeling) {
            this.sumPGAAangever = this.incomeTaxEntity.aangifte?.verdeling.aandeelPGA
                ? this.incomeTaxEntity.aangifte?.verdeling.aandeelPGA
                : 0;
        } else if (this.berekening.opbouwPGA !== undefined) {
            const sumZorgkosten = this.berekening.opbouwPGA?.specifiekeZorgkosten ? this.berekening.opbouwPGA?.specifiekeZorgkosten : 0;
            const sumGiften = this.berekening?.opbouwPGA?.aftrekbareGiften ? this.berekening.opbouwPGA?.aftrekbareGiften : 0;
            const sumScholing = this.berekening?.opbouwPGA?.scholingsuitgaven ? this.berekening.opbouwPGA?.scholingsuitgaven : 0;
            const sumWeekend = this.berekening?.opbouwPGA?.weekenduitgaven ? this.berekening.opbouwPGA?.weekenduitgaven : 0;
            const sumOnderhoud = this.berekening?.opbouwPGA?.onderhoudsverplichtingen
                ? this.berekening.opbouwPGA?.onderhoudsverplichtingen
                : 0;
            const sumRestant = this.berekening?.opbouwPGA?.restantPgaVoorgaandeJaren
                ? this.berekening.opbouwPGA?.restantPgaVoorgaandeJaren
                : 0;
            this.sumPGAAangever = sumZorgkosten + sumGiften + sumScholing + sumWeekend + sumOnderhoud + sumRestant;
        }
    }

    getSumPGAPartner() {
        if (
            this.incomeTaxEntity.aangifte &&
            this.incomeTaxEntity.aangifte?.partnerAangifte &&
            this.incomeTaxEntity.aangifte?.partnerAangifte.verdeling
        ) {
            this.sumPGAPartner = this.incomeTaxEntity.aangifte?.partnerAangifte?.verdeling.aandeelPGA
                ? this.incomeTaxEntity.aangifte?.partnerAangifte?.verdeling.aandeelPGA
                : 0;
        }
    }

    getSumVoorheffingen() {
        if (this.incomeTaxEntity.aangifte?.voorheffingen?.voorlopigeAanslag !== undefined) {
            this.sumVAIB = this.incomeTaxEntity.aangifte?.voorheffingen?.voorlopigeAanslag;
        }
        if (this.incomeTaxEntity.aangifte?.voorheffingen?.voorlopigeAanslagZvw !== undefined) {
            this.sumVAZVW = this.incomeTaxEntity.aangifte?.voorheffingen?.voorlopigeAanslagZvw;
        }
        if (this.incomeTaxEntity.aangifte?.voorheffingen?.ingehoudenDividendbelasting !== undefined) {
            if (this.incomeTaxEntity.aangifte?.verdeling) {
                this.sumDivBel = this.incomeTaxEntity.aangifte?.verdeling.aandeelDividendbelasting;
            } else this.sumDivBel = this.rapport.specificatieVoorheffingen?.opbouw?.dividendbelasting;
        }
        this.sumVoorheffingen =
            (this.sumVAIB ? this.sumVAIB : 0) + (this.sumVAZVW ? this.sumVAZVW : 0) + (this.sumDivBel ? this.sumDivBel : 0);
    }

    getSumVoorheffingenPartner() {
        if (
            this.incomeTaxEntity.aangifte?.partnerAangifte &&
            this.incomeTaxEntity.aangifte?.partnerAangifte.voorheffingen &&
            this.incomeTaxEntity.aangifte?.partnerAangifte.voorheffingen.voorlopigeAanslag &&
            this.incomeTaxEntity.aangifte?.partnerAangifte.voorheffingen.voorlopigeAanslag !== null
        ) {
            this.sumVAIBPartner = this.incomeTaxEntity.aangifte?.partnerAangifte.voorheffingen.voorlopigeAanslag;
        }
        if (this.incomeTaxEntity.aangifte?.partnerAangifte?.voorheffingen?.voorlopigeAanslagZvw !== undefined) {
            this.sumVAZVWPartner = this.incomeTaxEntity.aangifte?.partnerAangifte?.voorheffingen?.voorlopigeAanslagZvw;
        }
        if (this.incomeTaxEntity.aangifte?.verdeling) {
            this.sumDivBelPartner = this.incomeTaxEntity.aangifte?.partnerAangifte?.verdeling?.aandeelDividendbelasting
                ? this.incomeTaxEntity.aangifte?.partnerAangifte?.verdeling?.aandeelDividendbelasting
                : 0;
        } else if (this.incomeTaxEntity.aangifte?.partnerAangifte?.voorheffingen?.ingehoudenDividendbelasting !== undefined) {
            this.sumDivBelPartner = this.rapportPartner.specificatieVoorheffingen?.opbouw?.dividendbelasting;
        }
        this.sumVoorheffingenPartner =
            (this.sumVAIBPartner ? this.sumVAIBPartner : 0) +
            (this.sumVAZVWPartner ? this.sumVAZVWPartner : 0) +
            (this.sumDivBelPartner ? this.sumDivBelPartner : 0);
    }

    getRevisieRenteAangever() {
        if (this.incomeTaxEntity.aangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente !== undefined) {
            this.bedragRevisierente = this.incomeTaxEntity.aangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente;
        }
    }

    getRevisieRentePartner() {
        if (this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente !== undefined) {
            this.bedragRevisierentePartner = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente;
        }
    }

    getSumAanslagenIbenZvw() {
        if (this.berekening.verschuldigdeInkomstenbelasting !== null) {
            this.sumIB = this.berekening.verschuldigdeInkomstenbelasting;
        }
        if (this.berekening.verschuldigdeZVW !== null) {
            this.sumZVW = this.berekening.verschuldigdeZVW;
        }
        this.sumAanslagenIbenZvw = (this.sumIB ? this.sumIB : 0) + (this.sumZVW ? this.sumZVW : 0) + (this.bedragRevisierente ? this.bedragRevisierente : 0);
    }

    getSumAanslagenIbenZvwPartner() {
        if (this.berekening.partnerBerekening?.verschuldigdeInkomstenbelasting !== null) {
            this.sumIBPartner = this.berekening.partnerBerekening?.verschuldigdeInkomstenbelasting;
        }
        if (this.berekening.partnerBerekening?.verschuldigdeZVW !== null) {
            this.sumZVWPartner = this.berekening.partnerBerekening?.verschuldigdeZVW;
        }
        this.sumAanslagenIbenZvwPartner = (this.sumIBPartner ? this.sumIBPartner : 0) + (this.sumZVWPartner ? this.sumZVWPartner : 0) + (this.bedragRevisierentePartner ? this.bedragRevisierentePartner : 0);
    }

    getNaamAangever(incomeTaxEntity: IncomeTaxEntity) {
        if (incomeTaxEntity.aangifte?.aangever !== undefined) {
            if (incomeTaxEntity.aangifte?.aangever != null && incomeTaxEntity.aangifte?.aangever.achternaam != null) {
                let voorletters = '';
                let tussenvoegsel = '';
                let achternaam = '';
                if (incomeTaxEntity.aangifte?.aangever.voorletters) {
                    voorletters = incomeTaxEntity.aangifte?.aangever.voorletters.replace(/\./g, '');
                }
                if (incomeTaxEntity.aangifte?.aangever.tussenvoegsels) {
                    tussenvoegsel = incomeTaxEntity.aangifte?.aangever.tussenvoegsels;
                }
                if (incomeTaxEntity.aangifte?.aangever.achternaam) {
                    achternaam = incomeTaxEntity.aangifte?.aangever.achternaam;
                }
                if (tussenvoegsel) {
                    this.naamAangever = voorletters + ' ' + tussenvoegsel + ' ' + achternaam;
                } else {
                    this.naamAangever = voorletters + ' ' + achternaam;
                }
            } else {
                this.naamAangever = 'niet ingevuld';
            }
        }
    }

    getNaamPartner() {
        if (this.incomeTaxEntity.aangifte?.partnerAangifte) {
            if (
                this.incomeTaxEntity.aangifte?.partnerAangifte != null &&
                this.incomeTaxEntity.aangifte?.partnerAangifte.aangever != null &&
                this.incomeTaxEntity.aangifte?.partnerAangifte.aangever.achternaam != null
            ) {
                let voorletters = '';
                let tussenvoegsel = '';
                let achternaam = '';
                if (this.incomeTaxEntity.aangifte?.partnerAangifte.aangever.voorletters) {
                    voorletters = this.incomeTaxEntity.aangifte?.partnerAangifte.aangever.voorletters.replace(/\./g, '');
                }
                if (this.incomeTaxEntity.aangifte?.partnerAangifte.aangever.tussenvoegsels) {
                    tussenvoegsel = this.incomeTaxEntity.aangifte?.partnerAangifte.aangever.tussenvoegsels;
                }
                if (this.incomeTaxEntity.aangifte?.partnerAangifte.aangever.achternaam) {
                    achternaam = this.incomeTaxEntity.aangifte?.partnerAangifte.aangever.achternaam;
                }
                if (tussenvoegsel) {
                    this.naamPartner = voorletters + ' ' + tussenvoegsel + ' ' + achternaam;
                } else {
                    this.naamPartner = voorletters + ' ' + achternaam;
                }
            } else {
                this.naamPartner = 'niet ingevuld';
            }
        }
    }

    config = ContextMenuConfiguration.create({
        icon: 'ellipsis-vertical',
        iconType: 'light',
        definition: (menu) => menu.createGroup('Reminders').and.createGroup(),
    });

    fetchData() {
        this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            if (incomeTaxEntity.aangifte) {
                this.incomeTaxEntity.aangifte = incomeTaxEntity.aangifte;
                this.changeDetector.detectChanges();
            }
        });
    }

  protected readonly JSON = JSON;
}
